import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_MIN_PX, PANEL_MIN_PX } from '../splitBounds';

/** Share of the split taken by the panel column. 0.6 reproduces the former fixed `3fr 2fr`. */
const DEFAULT_RATIO = 0.6;

/** Keeps the handle off either edge; the px floors below do the rest. */
const MIN_RATIO = 0.3;
const MAX_RATIO = 0.72;

/** Reported by the handle's `role="separator"`. */
export const MIN_SPLIT_RATIO = MIN_RATIO;
export const MAX_SPLIT_RATIO = MAX_RATIO;

const STORAGE_KEY = 'guardian.dualPanel.splitRatio';

const clamp = (value: number): number => Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));

const readStoredRatio = (): number => {
  try {
    const stored = Number.parseFloat(window.localStorage.getItem(STORAGE_KEY) ?? '');
    return Number.isFinite(stored) ? clamp(stored) : DEFAULT_RATIO;
  } catch {
    // Private mode, or storage disabled — the default is a fine answer.
    return DEFAULT_RATIO;
  }
};

export type SplitRatio = {
  ratio: number;
  /** True while dragging: the caller suppresses the column transition so the handle tracks 1:1. */
  isDragging: boolean;
  onHandlePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  /** Keyboard resizing, so the split isn't mouse-only. */
  onHandleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  resetRatio: () => void;
};

/**
 * The draggable split, as a ratio so the proportion survives a window resize. Remembered across
 * sessions: a split is a workspace preference.
 */
export const useSplitRatio = (containerRef: RefObject<HTMLElement | null>): SplitRatio => {
  const [ratio, setRatio] = useState<number>(readStoredRatio);
  const [isDragging, setIsDragging] = useState(false);
  // The committed value is written on release, not on every frame of the drag.
  const latestRatio = useRef(ratio);

  const persist = useCallback((value: number) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // Not being able to remember the split is not worth failing a drag over.
    }
  }, []);

  const applyFromClientX = useCallback(
    (clientX: number) => {
      const container = containerRef.current;
      if (!container) return;
      const { left, width } = container.getBoundingClientRect();
      if (width === 0) return;

      // Clamped in px, not just as a ratio: 28% is comfortable on a wide window and unusable on
      // a narrow one. Ordered so a window too small for both floors pins to the panel's.
      const lower = Math.min(PANEL_MIN_PX, width);
      const upper = Math.max(lower, width - CHAT_MIN_PX);
      const offset = Math.min(Math.max(clientX - left, lower), upper);

      const next = clamp(offset / width);
      latestRatio.current = next;
      setRatio(next);
    },
    [containerRef],
  );

  const onHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDragging(true);
      applyFromClientX(event.clientX);
    },
    [applyFromClientX],
  );

  // Listeners on the window, not the handle: the pointer routinely outruns a 6px target mid-drag.
  useEffect(() => {
    if (!isDragging) return;

    const onMove = (event: PointerEvent) => applyFromClientX(event.clientX);
    const onUp = () => {
      setIsDragging(false);
      persist(latestRatio.current);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, applyFromClientX, persist]);

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const step = event.key === 'ArrowLeft' ? -0.02 : event.key === 'ArrowRight' ? 0.02 : 0;
      if (step === 0) return;
      event.preventDefault();
      const next = clamp(latestRatio.current + step);
      latestRatio.current = next;
      setRatio(next);
      persist(next);
    },
    [persist],
  );

  const resetRatio = useCallback(() => {
    latestRatio.current = DEFAULT_RATIO;
    setRatio(DEFAULT_RATIO);
    persist(DEFAULT_RATIO);
  }, [persist]);

  return { ratio, isDragging, onHandlePointerDown, onHandleKeyDown, resetRatio };
};
